import React from "react";
import styled from "styled-components";
import { authAPI } from "../../../api/auth";
import { Profile } from "../../../api/auth/myVerifiedProfiles";
import { useLatestQuery } from "../../../api/useLatestQuery";
import { formatRelativeDate } from "../../../app/pages/CvpEnter/formatRelativeDate";
import { activateProfileAndRedirect } from "../../../app/pages/CvpEnter/helpers";
import { useAvatarColors } from "../../../app/pages/CvpEnter/useAvatarColors";
import {
  showErrorToastMsg,
  showToastMsg,
} from "../../../generic-components/messages/CvpToastMessages";
import { useLocStore } from "../../../localization";
import { LocContext } from "../../../localization/LocContext";
import { useSettings } from "../../../settings/useSettings";
import { useLogOut } from "../../../url/logOut";
import { getRedirectProps } from "../../../url/redirect";
import { useCurrentPath } from "../../../url/useCurrentPath";
import { useLoginProps } from "../../../url/useLoginProps";
import { useWhoAmI } from "../../../user/useWhoAmI";
import {
  useShowVpnTools
} from "../../adminSettings/AdminContext";
import { CustomMenuItem } from "../MenuItems/completeMenuItem";
import { useEnableParameterOverridesMenuItem } from "./useEnableParameterOverridesMenuItem";
import { useLanguageMenuItem } from "./useLanguageMenuItem";
import { useLoginToOldPortalMenuItem } from "./useLoginToOldPortalMenuItem";
import { useVpnToolsMenuItem } from "./useVpnToolsMenuItem";

export interface UseProfileDepartmentItemsProps {
  showConcernItems?: boolean;
  showMyProfile?: boolean;
  showLogOut?: boolean;
}
export function useProfileDepartmentItems(): CustomMenuItem[] {
  const L = useLocStore(LocContext.PROFILE);
  const { confirmedLoggedIn, confirmedNotLoggedIn, whoAmI } = useWhoAmI();

  const logOut = useLogOut();
  const languageMenuItem = useLanguageMenuItem();
  const settings = useSettings();
  const vpnToolsMenuItem = useVpnToolsMenuItem();

  const enableParameterOverridesMenuItem = useEnableParameterOverridesMenuItem();
  const showVpnTools = useShowVpnTools();
  const myProfilesMenuItem = useMyProfilesMenuItem();
  const loginToOldPortalMenuItem = useLoginToOldPortalMenuItem();

  const showLogOut = confirmedLoggedIn;
  const showLogIn = confirmedNotLoggedIn;

  let { redirectProps: loginRedirectProps } = useLoginProps({
    cvpalru: "/profile",
  });

  const items: CustomMenuItem[] = [];
  if (!settings?.iframe_overrideLocale) {
    items.push(languageMenuItem);
  }

  if (window.isLocalIp) {
    items.splice(
      0,
      0,
      {
        path: "",
        label: L.id("a2e7c193f5b0").loc("VPN-verktøy"),
        icon: "pi pi-fw pi-wrench",
        className: "vpn-item",
        items: [vpnToolsMenuItem, enableParameterOverridesMenuItem],
        isSelected: () => false,
      } as CustomMenuItem,
      {
        path: "/admin",
        target: "_blank",
        ...getRedirectProps("/admin", {
          mergeQs: {},
          target: "_blank",
        }),
        label: L.id("4e451af785fa").loc("Admin"),
        icon: "pi pi-fw pi-cog",
        className: "vpn-item",
      }
    );

    // Right after the VPN group: jump the current candidate into the old portal
    // (cvp1). Null when there is no candidate/concern in this session.
    if (loginToOldPortalMenuItem) {
      items.splice(2, 0, loginToOldPortalMenuItem);
    }
  }

  if (confirmedLoggedIn && myProfilesMenuItem) {
    items.push(myProfilesMenuItem);
  }

  const SHOW_KEYCLOAK_PROFILE_MANAGEMENT = showVpnTools;

  // Add Manage Account link for Keycloak users (change password, update MFA, etc.)
  if (
    SHOW_KEYCLOAK_PROFILE_MANAGEMENT &&
    confirmedLoggedIn &&
    whoAmI.cvpIdp === "Keycloak"
  ) {
    items.push({
      href: "https://auth.webtemp.no/realms/CVP/account",
      target: "_blank",
      label: L.id("cb0b6af5f73a").loc("Administrer konto (avansert)"),
      icon: "pi pi-fw pi-key",
      isSelected: () => false,
      className: "vpn-item",
    });
  }

  if (showLogOut) {
    items.push({
      label: L.id("72bd38a1ab66").loc("Logg ut"),
      icon: "pi pi-fw pi-sign-out",
      className: "log-out",
      onClick: logOut,
    });
  }

  if (showLogIn) {
    items.push({
      ...loginRedirectProps,
      label: L.id("a918c8de02b7").loc("Logg inn"),
      icon: "pi pi-fw pi-sign-in",
    });
  }

  if (confirmedLoggedIn) {
    items.push({
      path: "",
      icon: "pi pi-fw pi-copy",
      label: L.id("3acf453f1aec").split("Bruker-ID: {0}", [
        whoAmI?.userIdFromIdentityProvider,
      ]),
      className: "guid",
      onClick: async () => {
        try {
          await navigator.clipboard.writeText(whoAmI?.userIdFromIdentityProvider);
          showToastMsg(L.id("565b477e2a72").loc("Bruker-ID kopiert til utklippstavlen"));
        } catch (err) {
          showErrorToastMsg(err);
          console.log("Something went wrong", err);
        }
      },
    });
  }

  return items;
}

// Expands "Mine profiler" into a submenu listing the user's verified profiles
// for one-click activation, with a "Administrer profiler" footer that opens the
// full CvpEnter page. Falls back to a flat /cvpenter link while the list is
// empty (no profiles, or query still loading).
function useMyProfilesMenuItem(): CustomMenuItem {
  const L = useLocStore(LocContext.PROFILE);
  const { whoAmI } = useWhoAmI();
  const currentPath = useCurrentPath();
  const selectedCandidateId =
    whoAmI?.selectedVerifiedProfileInConcernForThisSession ?? null;

  const { data: profilesResult } = useLatestQuery(["authAPI.myVerifiedProfiles"], () =>
    authAPI.myVerifiedProfiles({})
  );
  const verifiedProfiles = profilesResult?.verifiedProfiles ?? [];

  if (window.iswtapp) {
    return null;
  }

  const baseItem: CustomMenuItem = {
    path: "/cvpenter",
    label: L.id("7660df638826").loc("Mine profiler"),
    icon: "pi pi-fw pi-user",
  };
  if (verifiedProfiles.length === 0) return baseItem;

  const groups = groupProfilesForMenu(verifiedProfiles);
  const groupedItems: CustomMenuItem[] = [];
  groups.forEach(({ concernName, profiles }, groupIndex) => {
    if (groupIndex > 0) groupedItems.push({ separator: true } as CustomMenuItem);
    groupedItems.push({
      label: concernName,
      className: "menu-section-header",
      disabled: true,
      template: () => (
        <SectionHeader>
          <i className="pi pi-fw pi-building" aria-hidden="true" />
          {concernName}
        </SectionHeader>
      ),
    });
    for (const p of profiles) {
      const isSelected = p.candidateId === selectedCandidateId;
      groupedItems.push({
        label: p.concernName,
        className: "cvp-profile-row",
        isSelected: () => isSelected,
        onClick: () =>
          activateProfileAndRedirect(
            {
              concernId: p.concernId,
              candidateId: p.candidateId,
            },
            currentPath
          ),
        template: () => <ProfileMenuItemTemplate profile={p} isSelected={isSelected} />,
      });
    }
  });

  return {
    ...baseItem,
    items: [
      ...groupedItems,
      { separator: true } as CustomMenuItem,
      {
        path: "/cvpenter",
        label: L.id("ed9303b10e5f").loc("Administrer profiler"),
        icon: "pi pi-fw pi-cog",
        className: "profile-manage-link",
      },
    ],
  };
}

// Group by concernName. Groups whose most-recently-used profile is newest
// appear first; ties fall back to concern-name alpha. Within each group,
// profiles sort by candidateEmail asc, then lastUsedAt desc.
function groupProfilesForMenu(
  profiles: Profile[]
): { concernName: string; profiles: Profile[] }[] {
  const ts = (s: string | null | undefined) => (s ? new Date(s).getTime() : 0);
  const groups = new Map<string, Profile[]>();
  for (const p of profiles) {
    const key = p.concernName ?? "";
    if (!groups.has(key)) groups.set(key, []);
    groups.get(key)!.push(p);
  }
  const groupMaxLastUsed = (g: Profile[]) =>
    g.reduce((max, p) => Math.max(max, ts(p.lastUsedAt)), 0);

  return [...groups.entries()]
    .sort(([aName, a], [bName, b]) => {
      const diff = groupMaxLastUsed(b) - groupMaxLastUsed(a);
      return diff !== 0 ? diff : aName.localeCompare(bName);
    })
    .map(([concernName, g]) => ({
      concernName,
      profiles: [...g].sort((a, b) => {
        const ae = a.candidateEmail ?? "";
        const be = b.candidateEmail ?? "";
        const emailDiff = ae.localeCompare(be);
        if (emailDiff !== 0) return emailDiff;
        return ts(b.lastUsedAt) - ts(a.lastUsedAt);
      }),
    }));
}

const ProfileMenuItemTemplate: React.FC<{
  profile: Profile;
  isSelected: boolean;
}> = ({ profile, isSelected }) => {
  const L = useLocStore(LocContext.PROFILE);
  const color = useAvatarColors().get(profile.candidateId);
  // When a concern is picked (k= in url → window.cvpIdConcern), show the
  // candidate name as the title instead of the email, and move the email into
  // the meta line. Falls back to email/concernName when no name is available.
  const showName = !!window.cvpIdConcern && !!profile.candidateName;
  return (
    <StyledProfileMenuItem
      data-selected={isSelected ? "true" : undefined}
      data-avatar-color={color}
    >
      <span className="profile-menu-item__avatar">
        <i className="pi pi-user" aria-hidden="true" />
      </span>
      <div className="profile-menu-item__text">
        <div className="profile-menu-item__email">
          <span className="profile-menu-item__name">
            {showName
              ? profile.candidateName
              : profile.candidateEmail || profile.concernName}
          </span>
          {showName && (
            <span className="profile-menu-item__id">#{profile.candidateId}</span>
          )}
        </div>
        <div className="profile-menu-item__meta">
          {(() => {
            const lead = showName ? (
              profile.candidateEmail ? (
                <span>{profile.candidateEmail}</span>
              ) : null
            ) : (
              <span className="profile-menu-item__id">#{profile.candidateId}</span>
            );
            return (
              <>
                {lead}
                {profile.lastUsedAt && (
                  <>
                    {lead && <span className="profile-menu-item__dot">·</span>}
                    <span>
                      {L.id("dc891bc21528").loc("Sist brukt")}{" "}
                      {formatRelativeDate(profile.lastUsedAt)}
                    </span>
                  </>
                )}
              </>
            );
          })()}
        </div>
      </div>
      {isSelected && (
        <span className="profile-menu-item__pill">
          {L.id("1fe48ae358cc").loc("Aktiv")}
        </span>
      )}
    </StyledProfileMenuItem>
  );
};

const SectionHeader = styled.div`
  display: flex;
  align-items: center;
  gap: 0.6rem;
  padding: 0.1rem 0;
  font-size: 0.82rem;
  font-weight: 700;
  color: var(--text-color);

  > i.pi-building {
    color: var(--text-color-secondary);
    font-size: 0.85rem;
  }
`;

const StyledProfileMenuItem = styled.div`
  display: grid;
  grid-template-columns: auto 1fr auto;
  align-items: center;
  gap: 0.7rem;
  width: 100%;

  .profile-menu-item__avatar {
    width: 28px;
    height: 28px;
    border-radius: 50%;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    background: var(--surface-200, var(--surface-100));
    color: var(--text-color-secondary);
    flex: 0 0 auto;

    > i {
      font-size: 0.72rem;
      line-height: 1;
    }
  }

  &[data-avatar-color="blue"] .profile-menu-item__avatar {
    background: var(--blue-100);
    color: var(--blue-600);
  }
  &[data-avatar-color="indigo"] .profile-menu-item__avatar {
    background: var(--indigo-100);
    color: var(--indigo-600);
  }
  &[data-avatar-color="purple"] .profile-menu-item__avatar {
    background: var(--purple-100);
    color: var(--purple-600);
  }
  &[data-avatar-color="pink"] .profile-menu-item__avatar {
    background: var(--pink-100);
    color: var(--pink-600);
  }
  &[data-avatar-color="cyan"] .profile-menu-item__avatar {
    background: var(--cyan-100);
    color: var(--cyan-600);
  }
  &[data-avatar-color="teal"] .profile-menu-item__avatar {
    background: var(--teal-100);
    color: var(--teal-600);
  }
  &[data-avatar-color="orange"] .profile-menu-item__avatar {
    background: var(--orange-100);
    color: var(--orange-600);
  }
  &[data-avatar-color="yellow"] .profile-menu-item__avatar {
    background: var(--yellow-100);
    color: var(--yellow-600, #b45309);
  }

  .profile-menu-item__text {
    display: flex;
    flex-direction: column;
    gap: 0.1rem;
    min-width: 0;
  }

  .profile-menu-item__email {
    font-size: 0.85rem;
    font-weight: 500;
    color: var(--text-color);
    display: flex;
    align-items: baseline;
    gap: 0.4rem;
    min-width: 0;
  }

  .profile-menu-item__name {
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }

  &[data-selected="true"] .profile-menu-item__email {
    font-weight: 600;
  }

  .profile-menu-item__meta {
    font-size: 0.72rem;
    color: var(--text-color-secondary);
    display: inline-flex;
    align-items: center;
    gap: 0.4rem;
  }

  .profile-menu-item__id {
    font-family: ui-monospace, monospace;
    font-size: 0.72rem;
    font-weight: 500;
    color: var(--text-color-secondary);
    flex: 0 0 auto;
  }

  .profile-menu-item__dot {
    opacity: 0.5;
  }

  .profile-menu-item__pill {
    font-size: 0.65rem;
    font-weight: 700;
    letter-spacing: 0.08em;
    text-transform: uppercase;
    color: #fff;
    padding: 0.18rem 0.5rem;
    border-radius: 999px;
    background: var(--primary-500);
    white-space: nowrap;
  }
`;
